// src/views/Deposits.js
import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  Button,
  Input,
  FormGroup,
  Label,
  Container,
  Row,
  Col,
  Spinner,
  Progress
} from "reactstrap";
import axios from "axios";
import Swal from "sweetalert2";
import { Enviroments } from "../enviroments/enviroments";
import { cactusApi } from "../services/cactusApi";

const Deposits = () => {
  const [amountStr, setAmountStr] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [pixDetails, setPixDetails] = useState(null); // { transaction_id, qr_code, br_code, amount }
  const [externalId, setExternalId] = useState(null); // transaction_id
  const [paymentStatus, setPaymentStatus] = useState("idle");

  const [countdown, setCountdown] = useState(400); 
  const pollRef = useRef(null);
  const timerRef = useRef(null);

  const cpfLocal = (localStorage.getItem("cpf") || "").replace(/\D/g, "");
  const iaHeaders = { headers: { Authorization: `Bearer ${localStorage.getItem("casinoToken") || localStorage.getItem("apiToken") || ""}` } };

  const extractError = (err) => {
    const d = err?.response?.data;
    if (!d) return err?.message || "Falha de rede.";
    if (d.message) return d.message;
    if (typeof d === "string") return d;
    return "Erro ao processar a solicitação.";
  };

  const toCents = (s) => {
    if (typeof s !== "string") return 0;
    const norm = s.replace(/[^\d,.\s]/g, "").replace(/\s+/g, "").replace(",", ".");
    const n = Number(norm);
    if (!Number.isFinite(n) || n <= 0) return 0;
    // O backend da Cactus parece esperar o valor normal (ex: 100 para R$100) ou em centavos?
    // O exemplo mostra body { amount: 100 } e response { amount: 100 }.
    // Se for valor inteiro, pode ser reais. Se for centavos, 100 = R$1,00.
    // Pelo padrão anterior, usava-se centavos. Mas o exemplo da Cactus mostra amount: 100.
    // Vou assumir que o backend espera o valor em REAIS ou o valor numérico puro.
    // Mas, por segurança, se a API anterior usava centavos e essa nova segue o padrão de pagamento,
    // é comum ser centavos OU o valor float.
    // O exemplo diz: Body { amount: 100 } -> Response { amount: 100 }.
    // Se o usuário digita 50,00 -> toCents vira 5000.
    // Vou enviar o valor NUMÉRICO FLOAT (ex: 50.00) se a API aceitar, ou inteiros.
    // O exemplo body { amount: 100 } parece ser R$ 100,00 se for integer, ou R$ 1,00 se for cents.
    // Vou ajustar o `toCents` para retornar o valor float normal para garantir, ou manter a conversão se confirmar.
    // Na dúvida, vou manter a lógica de input, mas na hora de enviar, enviarei o valor que o usuário digitou (float).
    return n;
  };

  const resetState = () => {
    setPixDetails(null);
    setExternalId(null);
    setPaymentStatus("idle");
    setCountdown(400);
    if (pollRef.current) clearInterval(pollRef.current);
    if (timerRef.current) clearInterval(timerRef.current);
    pollRef.current = null;
    timerRef.current = null;
  };

  const handleInputChange = (e) => {
    setAmountStr(e.target.value);
    setError("");
  };

  const handleDeposit = useCallback(async () => {
    setError("");
    // Se o usuário digita "100", toCents retorna 100.
    const amountVal = toCents(amountStr);
    
    if (!amountVal || amountVal <= 0) {
      setError("Por favor, informe um valor válido.");
      return;
    }

    try {
      setSubmitting(true);

      // 1) Cria depósito na Cactus API via serviço centralizado
      // Body: { amount: 100 }
      const { data: lg } = await cactusApi.createDeposit(amountVal);

      if (!lg?.success || !lg?.transaction_id) {
        throw new Error("Resposta inválida da plataforma de pagamento.");
      }

      // Mapeia resposta da Cactus
      // lg = { success: true, transaction_id, qr_code (url), br_code (copia e cola), amount, user_id }
      setPixDetails({
        transaction_id: lg.transaction_id,
        qr_code: lg.qr_code,
        br_code: lg.br_code,
        amount: lg.amount
      });
      setExternalId(lg.transaction_id);
      setPaymentStatus("pending");
      setSubmitting(false);

      // 2) Persiste localmente na SUA API (opcional, para histórico)
      try {
        await axios.post(
          `${Enviroments.API_URL_IA}deposits/save`,
          {
            external_id: lg.transaction_id,
            amount: lg.amount, // Salva o valor original
            status: "PENDING",
            payment_link: lg.qr_code,
            payment_code: lg.br_code,
            created_at: new Date().toISOString(),
            cpf: cpfLocal || undefined
          },
          iaHeaders
        );
      } catch (persistErr) {
        console.warn("Falha ao persistir localmente:", extractError(persistErr));
      }

      setCountdown(400);
    } catch (err) {
      setSubmitting(false);
      setError(extractError(err));
      setPaymentStatus("error");
    }
  }, [amountStr, cpfLocal]);

  // === POLLING de status na Cactus API ===
  const checkCactusStatus = useCallback(async () => {
    if (!externalId) return;
    try {
      const { data: st } = await cactusApi.getDepositStatus(externalId);
      // st = { success: true, transaction_id, status: 'completed' | 'pending', amount }
      
      const statusStr = String(st?.status || "").toLowerCase();
      const approved = statusStr === "completed" || statusStr === "paid" || statusStr === "approved";

      if (approved) {
        setPaymentStatus("approved");
        if (pollRef.current) clearInterval(pollRef.current);
        if (timerRef.current) clearInterval(timerRef.current);
        pollRef.current = null;
        timerRef.current = null;

        // Atualiza base local
        try {
          await axios.patch(
            `${Enviroments.API_URL_IA}deposits/${externalId}/status`,
            { status: "PAID" },
            iaHeaders
          );
        } catch (patchErr) {
          console.warn("Falha ao atualizar status local:", extractError(patchErr));
        }

        Swal.fire({
          title: "Pagamento aprovado!",
          text: "Seu depósito foi processado com sucesso.",
          icon: "success",
          confirmButtonText: "OK",
          confirmButtonColor: "#28a745"
        }).then(() => {
          resetState();
          window.location.reload();
        });
      }
    } catch (err) {
      // console.warn("Status Cactus:", extractError(err));
    }
  }, [externalId]);

  useEffect(() => {
    if (externalId && paymentStatus === "pending") {
      checkCactusStatus();
      pollRef.current = setInterval(checkCactusStatus, 5000);
      return () => {
        if (pollRef.current) clearInterval(pollRef.current);
      };
    }
  }, [externalId, paymentStatus, checkCactusStatus]);

  // Countdown + expiração do QR
  useEffect(() => {
    if (!pixDetails || paymentStatus !== "pending") return;

    timerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          if (pollRef.current) clearInterval(pollRef.current);
          if (timerRef.current) clearInterval(timerRef.current);
          pollRef.current = null;
          timerRef.current = null;
          setPaymentStatus("expired");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [pixDetails, paymentStatus]);

  // UI helpers
  const formatCountdown = () => {
    const m = String(Math.floor(countdown / 60)).padStart(2, "0");
    const s = String(countdown % 60).padStart(2, "0");
    return `${m}:${s}`;
  };

  const copyToClipboard = async (text) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      Swal.fire({
        toast: true,
        timer: 1500,
        position: "top",
        showConfirmButton: false,
        icon: "success",
        title: "Copiado!"
      });
    } catch {
      Swal.fire({
        toast: true,
        timer: 1800,
        position: "top",
        showConfirmButton: false,
        icon: "warning",
        title: "Não foi possível copiar"
      });
    }
  };

  // ===== RENDER =====

  if (submitting) {
    return (
      <Container className="text-center mt-5">
        <Spinner color="success" />
        <p>Gerando Pix, por favor aguarde...</p>
      </Container>
    );
  }

  if (pixDetails) {
    const isPending = paymentStatus === "pending";
    const isApproved = paymentStatus === "approved";
    const isExpired = paymentStatus === "expired";

    return (
      <Container className="content mt-5 mb-5">
        <Row>
          <Col md={{ size: 8, offset: 2 }}>
            <div className="text-center mb-3">
              <h2 className="text-success">Depósito Pix</h2>
              {isPending && (
                <>
                  <p className="text-danger fw-bold">Pague em: {formatCountdown()}</p>
                  <div className="d-flex justify-content-center align-items-center">
                    <Spinner size="sm" color="warning" className="me-2" />
                    <span className="text-warning">Aguardando pagamento...</span>
                  </div>
                </>
              )}
              {isApproved && <p className="text-success fw-bold">✅ Pagamento aprovado!</p>}
              {isExpired && <p className="text-muted fw-bold">⏱️ Tempo esgotado. Gere um novo Pix.</p>}
            </div>

            {isPending && (
              <Progress className="mb-3" value={((400 - countdown) / 400) * 100} />
            )}

            <div className="text-center mb-4">
              {pixDetails.qr_code ? (
                <img
                  src={pixDetails.qr_code}
                  alt="QR Code Pix"
                  className="img-fluid"
                  style={{
                    opacity: isApproved ? 0.5 : 1,
                    filter: isApproved ? "grayscale(100%)" : "none",
                    maxWidth: 320
                  }}
                />
              ) : (
                <div className="alert alert-warning">QR Code não disponível</div>
              )}

              <p className="mt-3">
                <strong>
                  Valor: R$ {Number(pixDetails.amount || 0).toFixed(2)}
                </strong>
              </p>

              {pixDetails.br_code && (
                <>
                  <p className="small text-muted mb-1">Copia e Cola</p>
                  <p className="text-break small">{pixDetails.br_code}</p>
                </>
              )}
            </div>

            <div className="d-flex justify-content-center gap-2">
              <Button
                color="primary"
                onClick={() => copyToClipboard(pixDetails.br_code || pixDetails.qr_code)}
                disabled={isApproved || isExpired}
              >
                Copiar Código Pix
              </Button>
              <Button color="success" onClick={resetState}>
                Novo
              </Button>
              <Button color="info" onClick={() => window.location.reload()}>
                Atualizar
              </Button>
            </div>

            {isPending && (
              <div className="text-center mt-3">
                <small className="text-muted">
                  Verificando pagamento automaticamente a cada 5 segundos...
                </small>
              </div>
            )}
            <p className="text-center mt-3 text-muted">
              Após pagar, a confirmação ocorre em alguns segundos.
            </p>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container className="content mt-5 mb-5">
      <Row>
        <Col md={{ size: 8, offset: 2 }}>
          <div className="text-center mb-4">
            <h2 className="text-success">Depósito Pix</h2>
            <p>Informe o valor para gerar o QR Code</p>
          </div>

          <FormGroup>
            <Label htmlFor="balanceInput" className="fw-bold">Valor do Depósito</Label>
            <Input
              id="balanceInput"
              inputMode="decimal"
              placeholder="Ex.: 50,00"
              value={amountStr}
              onChange={handleInputChange}
              className="text-center"
              disabled={submitting}
            />
            {error && <p className="text-danger mt-2">{error}</p>}
          </FormGroup>

          <div className="d-flex justify-content-around mt-3 flex-wrap gap-2">
            {[50, 100, 200, 500].map((v) => (
              <Button
                key={v}
                color="success"
                outline
                onClick={() => setAmountStr(String(v))}
                disabled={submitting}
              >
                R$ {v},00
              </Button>
            ))}
          </div>

          <div className="text-center mt-4">
            <Button color="primary" onClick={handleDeposit} disabled={submitting}>
              {submitting ? <Spinner size="sm" /> : "Gerar Pix"}
            </Button>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default Deposits;
